// Minimal RFC 4180 style CSV reader.
//
// Exports land here from Odoo and from spreadsheets, so the delimiter is not
// known up front and fields can be quoted, contain the delimiter, or contain
// escaped quotes. Splitting on commas mangles all three.

const DELIMITERS = [',', ';', '\t', '|'];

const CsvUtil = {
  /**
   * Splits raw CSV text into a grid of cells.
   * @param {string} text the file contents
   * @param {string} delimiter the field separator
   * @returns {Array} rows of string cells
   */
  parse: (text, delimiter = ',') => {
    if (!text) {
      return [];
    }

    // Excel writes a byte order mark that would otherwise glue itself to the
    // first header name.
    const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    let index = 0;

    while (index < input.length) {
      const character = input[index];

      if (inQuotes) {
        if (character === '"') {
          if (input[index + 1] === '"') {
            field += '"';
            index += 2;
            continue;
          }
          inQuotes = false;
          index++;
          continue;
        }
        field += character;
        index++;
        continue;
      }

      // A quote only opens a quoted field at the start of one, so a stray quote
      // mid value is kept as text.
      if (character === '"' && field === '') {
        inQuotes = true;
        index++;
        continue;
      }

      if (character === delimiter) {
        row.push(field);
        field = '';
        index++;
        continue;
      }

      if (character === '\r') {
        index++;
        continue;
      }

      if (character === '\n') {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
        index++;
        continue;
      }

      field += character;
      index++;
    }

    if (field !== '' || row.length > 0) {
      row.push(field);
      rows.push(row);
    }

    return rows;
  },

  /**
   * Picks the separator that yields the most consistent grid, so a semicolon or
   * tab export reads as well as a comma one.
   * @param {string} text the file contents
   * @returns {string} the winning delimiter
   */
  detectDelimiter: text => {
    let best = ',';
    let bestScore = 0;

    DELIMITERS.forEach(delimiter => {
      const rows = CsvUtil.parse(text, delimiter).slice(0, 20);
      if (rows.length === 0) {
        return;
      }

      // Score by how many rows agree on the most common column count. A wrong
      // delimiter leaves nearly every row a single column.
      const counts = {};
      rows.forEach(row => {
        counts[row.length] = (counts[row.length] || 0) + 1;
      });

      let modalWidth = 0;
      let modalRows = 0;
      Object.keys(counts).forEach(width => {
        if (counts[width] > modalRows || (counts[width] === modalRows && Number(width) > modalWidth)) {
          modalRows = counts[width];
          modalWidth = Number(width);
        }
      });

      const score = modalWidth < 2 ? 0 : modalWidth * modalRows;
      if (score > bestScore) {
        bestScore = score;
        best = delimiter;
      }
    });

    return best;
  },

  /**
   * Reads CSV text into a grid, detecting the delimiter first.
   * @param {string} text the file contents
   * @returns {Array} rows of string cells, blank rows removed
   */
  toGrid: text => {
    const rows = CsvUtil.parse(text, CsvUtil.detectDelimiter(text));
    return rows.filter(row => row.some(cell => (cell || '').trim() !== ''));
  },
};

export default CsvUtil;
